<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { saveSession, loadSession, clearSession } from '@engine/client/index';
import { gameDef, PLAYER_COLORS, type PlayerColor } from '../logic/game-logic';
import { getCompileSetupData } from '../logic/compile-cards';
import { SERVER_URL } from '../config';
import {
	useAuth,
	authHeaders,
	syncSessionToServer,
	deleteServerSession,
	fetchServerSessions,
} from '../composables/useAuth';

const POLL_INTERVAL = 3000;

const router = useRouter();

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

const {
	playerName,
	isLoggedIn,
	loginError,
	loading: authLoading,
	register,
	login,
	logout,
} = useAuth();
const authMode = ref<'login' | 'register'>('login');
const authName = ref('');
const authPin = ref('');

async function handleAuth() {
	const ok =
		authMode.value === 'register'
			? await register(authName.value, authPin.value)
			: await login(authName.value, authPin.value);
	if (ok) {
		authName.value = '';
		authPin.value = '';
		await restoreServerSessions();
		await refreshData();
	}
}

function handleLogout() {
	logout();
	myGames.value = [];
	openGames.value = [];
}

async function restoreServerSessions() {
	const sessions = await fetchServerSessions();
	for (const s of sessions) {
		if (s.gameName !== gameDef.id) continue;
		const existing = loadSession(gameDef.id, s.matchID);
		if (!existing) {
			saveSession(gameDef.id, s.matchID, {
				playerID: s.playerSeatID,
				credentials: s.credentials,
				playerName: s.playerName,
			});
		}
	}
}

// ---------------------------------------------------------------------------
// Match types
// ---------------------------------------------------------------------------

interface MatchPlayer {
	id: number;
	name?: string;
	data?: { color?: string };
}

interface MatchInfo {
	matchID: string;
	players: MatchPlayer[];
	gameover: unknown;
	createdAt: number;
	updatedAt: number;
}

interface MyGameInfo extends MatchInfo {
	myPlayerID: string;
	myName: string;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type ViewMode = 'browse' | 'hosting';
const viewMode = ref<ViewMode>('browse');

const myGames = ref<MyGameInfo[]>([]);
const openGames = ref<MatchInfo[]>([]);
const loading = ref(true);
const errorMsg = ref('');

const showCreateForm = ref(false);
const numPlayers = ref(gameDef.minPlayers);
const creating = ref(false);
const selectedColor = ref<PlayerColor>('red');

function buildDefaultSetupData(): Record<string, unknown> {
	const data: Record<string, unknown> = {};
	for (const opt of gameDef.setupOptions ?? []) {
		data[opt.id] = opt.default;
	}
	return data;
}
const setupData = ref<Record<string, unknown>>(buildDefaultSetupData());

const BASE_MAX_PLAYERS = 4;
const effectiveMaxPlayers = computed(() =>
	setupData.value.expansion ? gameDef.maxPlayers : Math.min(gameDef.maxPlayers, BASE_MAX_PLAYERS)
);
const playerCountFixed = computed(() => gameDef.minPlayers === gameDef.maxPlayers);
watch(effectiveMaxPlayers, max => {
	if (numPlayers.value > max) numPlayers.value = max;
});

const hostedMatchID = ref<string | null>(null);
const hostedPlayers = ref<MatchPlayer[]>([]);
const linkCopied = ref(false);

const joinModalMatchID = ref<string | null>(null);
const joinModalColor = ref<PlayerColor>('red');
function openJoinModal(game: MatchInfo) {
	joinModalMatchID.value = game.matchID;
	const taken = new Set(game.players.map(p => p.data?.color).filter(Boolean) as string[]);
	const available = PLAYER_COLORS.filter(c => !taken.has(c));
	joinModalColor.value = (available[0] as PlayerColor) ?? 'red';
}
function closeJoinModal() {
	joinModalMatchID.value = null;
}
const joinModalAvailableColors = computed(() => {
	if (!joinModalMatchID.value) return [];
	const game = openGames.value.find(g => g.matchID === joinModalMatchID.value);
	const taken = new Set((game?.players ?? []).map(p => p.data?.color).filter(Boolean) as string[]);
	return PLAYER_COLORS.filter(c => !taken.has(c));
});

let pollTimer: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchMatchList(): Promise<MatchInfo[]> {
	const res = await fetch(`${SERVER_URL}/games/${gameDef.id}`);
	if (!res.ok) return [];
	const data = await res.json();
	return (data.matches ?? []) as MatchInfo[];
}

async function fetchMatch(matchID: string): Promise<MatchInfo | null> {
	const res = await fetch(`${SERVER_URL}/games/${gameDef.id}/${matchID}`);
	if (!res.ok) return null;
	const data = (await res.json()) as MatchInfo;
	data.matchID = matchID;
	return data;
}

function getStoredSessions(): { matchID: string; playerID: string; playerName: string }[] {
	const sessions: { matchID: string; playerID: string; playerName: string }[] = [];
	const prefix = `bgf:session:${gameDef.id}:`;
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key?.startsWith(prefix)) {
			const matchID = key.slice(prefix.length);
			const session = loadSession(gameDef.id, matchID);
			if (session) {
				sessions.push({
					matchID,
					playerID: session.playerID,
					playerName: session.playerName,
				});
			}
		}
	}
	return sessions;
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function refreshData() {
	try {
		const allMatches = await fetchMatchList();
		const sessions = getStoredSessions();

		const myMatchIDs = new Set(sessions.map(s => s.matchID));

		const myGamesList: MyGameInfo[] = [];
		for (const session of sessions) {
			const match = allMatches.find(m => m.matchID === session.matchID);
			if (match) {
				myGamesList.push({
					...match,
					myPlayerID: session.playerID,
					myName: session.playerName,
				});
			}
		}
		myGames.value = myGamesList.sort((a, b) => b.updatedAt - a.updatedAt);

		openGames.value = allMatches
			.filter(m => {
				if (myMatchIDs.has(m.matchID)) return false;
				if (m.gameover) return false;
				return m.players.some(p => !p.name);
			})
			.sort((a, b) => b.createdAt - a.createdAt);
	} catch {
		errorMsg.value = 'Could not connect to game server';
	} finally {
		loading.value = false;
	}
}

// ---------------------------------------------------------------------------
// Game creation
// ---------------------------------------------------------------------------

async function createMatch() {
	creating.value = true;
	errorMsg.value = '';

	try {
		const name = playerName.value.trim() || 'Player';

		const createRes = await fetch(`${SERVER_URL}/games/${gameDef.id}/create`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', ...authHeaders() },
			body: JSON.stringify({
				numPlayers: numPlayers.value,
				setupData: gameDef.id === 'compile' ? getCompileSetupData() : setupData.value,
			}),
		});
		if (createRes.status === 403) throw new Error('Authentication required to create games');
		if (!createRes.ok) {
			const body = await createRes.text();
			let msg = 'Server rejected match creation';
			try {
				const err = body ? JSON.parse(body) : null;
				if (err?.error) msg = err.error;
				else if (err?.message) msg = err.message;
			} catch {
				if (body) msg = body;
			}
			throw new Error(msg);
		}
		const { matchID } = (await createRes.json()) as { matchID: string };

		const joinRes = await fetch(`${SERVER_URL}/games/${gameDef.id}/${matchID}/join`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				playerID: '0',
				playerName: name,
				data: { color: selectedColor.value },
			}),
		});
		if (!joinRes.ok) throw new Error('Failed to claim host seat');
		const { playerCredentials } = (await joinRes.json()) as { playerCredentials: string };

		saveSession(gameDef.id, matchID, {
			playerID: '0',
			credentials: playerCredentials,
			playerName: name,
			playerColor: selectedColor.value,
		});
		await syncSessionToServer(gameDef.id, matchID, '0', playerCredentials, name);

		hostedMatchID.value = matchID;
		showCreateForm.value = false;
		setupData.value = buildDefaultSetupData();
		viewMode.value = 'hosting';
		startHostPolling(matchID);
	} catch (e: unknown) {
		errorMsg.value = e instanceof Error ? e.message : 'Failed to create match';
	} finally {
		creating.value = false;
	}
}

// ---------------------------------------------------------------------------
// Join an open game
// ---------------------------------------------------------------------------

async function joinGame(matchID: string, color?: PlayerColor) {
	errorMsg.value = '';
	closeJoinModal();

	try {
		const name = playerName.value.trim() || 'Player';
		const colorToUse = color ?? joinModalColor.value;

		const match = await fetchMatch(matchID);
		if (!match) throw new Error('Match not found');

		const openSeat = match.players.find(p => !p.name);
		if (!openSeat) throw new Error('No open seats');
		const seatID = String(openSeat.id);

		const joinRes = await fetch(`${SERVER_URL}/games/${gameDef.id}/${matchID}/join`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				playerID: seatID,
				playerName: name,
				data: { color: colorToUse },
			}),
		});
		if (!joinRes.ok) throw new Error('Failed to claim seat');
		const { playerCredentials } = (await joinRes.json()) as { playerCredentials: string };

		saveSession(gameDef.id, matchID, {
			playerID: seatID,
			credentials: playerCredentials,
			playerName: name,
			playerColor: colorToUse,
		});
		await syncSessionToServer(gameDef.id, matchID, seatID, playerCredentials, name);

		router.push(`/game/${matchID}/${seatID}`);
	} catch (e: unknown) {
		errorMsg.value = e instanceof Error ? e.message : 'Failed to join game';
	}
}

// ---------------------------------------------------------------------------
// Abandon a game (only when not all seats are filled)
// ---------------------------------------------------------------------------

async function abandonGame(matchID: string) {
	errorMsg.value = '';
	const session = loadSession(gameDef.id, matchID);
	if (!session) return;

	try {
		const res = await fetch(`${SERVER_URL}/games/${gameDef.id}/${matchID}/leave`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				playerID: session.playerID,
				credentials: session.credentials,
			}),
		});
		if (!res.ok) throw new Error('Server rejected leave request');

		const botCredsKey = `bgf:bots:${gameDef.id}:${matchID}`;
		const botCreds = JSON.parse(localStorage.getItem(botCredsKey) || '{}');
		const botPlayerIDs = Object.keys(botCreds);
		if (botPlayerIDs.length > 0) {
			for (const botPid of botPlayerIDs) {
				try {
					await fetch(`${SERVER_URL}/games/${gameDef.id}/${matchID}/leave`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ playerID: botPid, credentials: botCreds[botPid] }),
					});
				} catch {
					/* best effort */
				}
			}
			localStorage.removeItem(botCredsKey);
		}
	} catch {
		// Even if the server call fails, clean up locally
	}

	clearSession(gameDef.id, matchID);
	await deleteServerSession(gameDef.id, matchID);
	await refreshData();
}

async function abandonHostedGame() {
	if (!hostedMatchID.value) return;
	await abandonGame(hostedMatchID.value);
	backToBrowse();
}

// ---------------------------------------------------------------------------
// Hosting / waiting room
// ---------------------------------------------------------------------------

function startHostPolling(matchID: string) {
	stopPolling();
	pollHostedMatch(matchID);
	pollTimer = setInterval(() => pollHostedMatch(matchID), 2000);
}

async function pollHostedMatch(matchID: string) {
	const match = await fetchMatch(matchID);
	if (!match) return;
	hostedPlayers.value = match.players;
}

const allSeatsFilled = computed(
	() => hostedPlayers.value.length > 0 && hostedPlayers.value.every(p => p.name)
);

const filledCount = computed(() => hostedPlayers.value.filter(p => p.name).length);

const fillingBots = ref(false);

function saveBotCredentials(mID: string, playerID: string, credentials: string) {
	const key = `bgf:bots:${gameDef.id}:${mID}`;
	const existing = JSON.parse(localStorage.getItem(key) || '{}');
	existing[playerID] = credentials;
	localStorage.setItem(key, JSON.stringify(existing));
}

async function fillWithBots() {
	if (!hostedMatchID.value) return;
	fillingBots.value = true;
	errorMsg.value = '';

	try {
		const emptySeats = hostedPlayers.value.filter(p => !p.name);
		for (let i = 0; i < emptySeats.length; i++) {
			const seatID = String(emptySeats[i].id);
			const botName = `Bot ${emptySeats[i].id}`;
			const res = await fetch(`${SERVER_URL}/games/${gameDef.id}/${hostedMatchID.value}/join`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ playerID: seatID, playerName: botName }),
			});
			if (!res.ok) throw new Error(`Failed to seat ${botName}`);
			const { playerCredentials } = (await res.json()) as { playerCredentials: string };
			saveBotCredentials(hostedMatchID.value, seatID, playerCredentials);
		}
		await pollHostedMatch(hostedMatchID.value);
	} catch (e: unknown) {
		errorMsg.value = e instanceof Error ? e.message : 'Failed to fill bots';
	} finally {
		fillingBots.value = false;
	}
}

async function fillBotsForGame(matchID: string) {
	errorMsg.value = '';
	try {
		const match = await fetchMatch(matchID);
		if (!match) throw new Error('Match not found');

		const emptySeats = match.players.filter(p => !p.name);
		for (const seat of emptySeats) {
			const seatID = String(seat.id);
			const res = await fetch(`${SERVER_URL}/games/${gameDef.id}/${matchID}/join`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ playerID: seatID, playerName: `Bot ${seatID}` }),
			});
			if (!res.ok) throw new Error(`Failed to seat Bot ${seatID}`);
			const { playerCredentials } = (await res.json()) as { playerCredentials: string };
			saveBotCredentials(matchID, seatID, playerCredentials);
		}
		await refreshData();
	} catch (e: unknown) {
		errorMsg.value = e instanceof Error ? e.message : 'Failed to add bots';
	}
}

function inviteLink(): string {
	if (!hostedMatchID.value) return '';
	return `${window.location.origin}/join/${hostedMatchID.value}`;
}

async function copyLink() {
	try {
		await navigator.clipboard.writeText(inviteLink());
		linkCopied.value = true;
		setTimeout(() => {
			linkCopied.value = false;
		}, 2000);
	} catch {
		errorMsg.value = 'Clipboard access denied';
	}
}

function startGame() {
	if (!hostedMatchID.value) return;
	stopPolling();
	router.push(`/game/${hostedMatchID.value}/0`);
}

function backToBrowse() {
	stopPolling();
	viewMode.value = 'browse';
	hostedMatchID.value = null;
	hostedPlayers.value = [];
	refreshData();
}

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------

function seatSummary(players: MatchPlayer[]): string {
	const filled = players.filter(p => p.name).length;
	return `${filled}/${players.length}`;
}

function matchStatus(match: MatchInfo): 'waiting' | 'playing' | 'finished' {
	if (match.gameover) return 'finished';
	if (match.players.every(p => p.name)) return 'playing';
	return 'waiting';
}

// ---------------------------------------------------------------------------
// Polling & lifecycle
// ---------------------------------------------------------------------------

function stopPolling() {
	if (pollTimer) {
		clearInterval(pollTimer);
		pollTimer = null;
	}
}

function startBrowsePolling() {
	stopPolling();
	pollTimer = setInterval(refreshData, POLL_INTERVAL);
}

onMounted(async () => {
	if (isLoggedIn.value) {
		await restoreServerSessions();
	}
	await refreshData();
	startBrowsePolling();
});

onUnmounted(stopPolling);
</script>

<template>
	<div class="min-h-screen text-white" style="background: var(--cyber-bg);">
		<!-- Header -->
		<header class="border-b border-cyan-500/20" style="background: var(--cyber-surface);">
			<div class="max-w-5xl mx-auto px-6 py-8">
				<div class="flex items-start justify-between">
					<div>
						<h1 class="text-4xl font-bold tracking-tight font-display text-cyan-100" style="text-shadow: 0 0 20px rgba(34, 211, 238, 0.3);">{{ gameDef.displayName }}</h1>
						<p class="mt-2 text-slate-400">{{ gameDef.description }}</p>
					</div>
					<div v-if="isLoggedIn" class="flex items-center gap-3 shrink-0">
						<span class="text-sm text-slate-400"
							>Signed in as <strong class="text-cyan-200">{{ playerName }}</strong></span
						>
						<button
							class="px-3 py-1.5 text-xs text-cyan-400/80 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50 rounded-lg transition-colors"
							@click="handleLogout"
						>
							Sign Out
						</button>
					</div>
				</div>
			</div>
		</header>

		<!-- Main content -->
		<main class="max-w-5xl mx-auto px-6 py-8">
			<!-- Auth gate -->
			<template v-if="!isLoggedIn">
				<div class="max-w-sm mx-auto mt-8 space-y-6">
					<div class="text-center">
						<h2 class="text-xl font-semibold">
							{{ authMode === 'login' ? 'Sign In' : 'Create Account' }}
						</h2>
						<p class="mt-1 text-sm text-slate-400">
							{{
								authMode === 'login'
									? 'Sign in to create and rejoin games from any device.'
									: 'Pick a name and a PIN to get started.'
							}}
						</p>
					</div>

					<div class="space-y-3">
						<div>
							<label class="block text-sm font-medium text-slate-400 mb-1">Name</label>
							<input
								v-model="authName"
								placeholder="Your display name"
								maxlength="24"
								class="w-full px-4 py-2.5 rounded-lg text-white placeholder-slate-500 transition-colors border border-cyan-500/30 focus:border-cyan-400"
								style="background: var(--cyber-panel);"
								@keyup.enter="handleAuth"
							/>
						</div>
						<div>
							<label class="block text-sm font-medium text-slate-400 mb-1">PIN</label>
							<input
								v-model="authPin"
								type="password"
								placeholder="4-8 characters"
								maxlength="8"
								class="w-full px-4 py-2.5 rounded-lg text-white placeholder-slate-500 transition-colors border border-cyan-500/30 focus:border-cyan-400"
								style="background: var(--cyber-panel);"
								@keyup.enter="handleAuth"
							/>
						</div>
					</div>

					<p v-if="loginError" class="text-sm text-red-400 text-center">{{ loginError }}</p>

					<button
						class="w-full py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 font-display text-cyan-950 border border-cyan-400/50"
						style="background: linear-gradient(180deg, #22d3ee 0%, #0891b2 100%); box-shadow: 0 0 16px rgba(34, 211, 238, 0.4);"
						:disabled="authLoading || !authName.trim() || authPin.length < 4"
						@click="handleAuth"
					>
						{{
							authLoading ? 'Please wait...' : authMode === 'login' ? 'Sign In' : 'Create Account'
						}}
					</button>

					<p class="text-center text-sm text-slate-500">
						<template v-if="authMode === 'login'">
							Don't have an account?
							<button
								class="text-cyan-400 hover:text-cyan-300 font-medium"
								@click="
									authMode = 'register';
									loginError = '';
								"
							>
								Create one
							</button>
						</template>
						<template v-else>
							Already have an account?
							<button
								class="text-cyan-400 hover:text-cyan-300 font-medium"
								@click="
									authMode = 'login';
									loginError = '';
								"
							>
								Sign in
							</button>
						</template>
					</p>
				</div>
			</template>

			<!-- Logged-in content -->
			<template v-else>
				<!-- Error banner -->
				<div
					v-if="errorMsg"
					class="mb-6 p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-sm text-red-400 flex items-center justify-between"
				>
					<span>{{ errorMsg }}</span>
					<button class="text-red-400 hover:text-red-300 ml-4" @click="errorMsg = ''">
						Dismiss
					</button>
				</div>

				<!-- Loading -->
				<div v-if="loading" class="text-center py-16">
					<p class="text-cyan-400/80">Connecting to server...</p>
				</div>

				<!-- BROWSE MODE -->
				<template v-else-if="viewMode === 'browse'">
					<!-- Create game button -->
					<div class="mb-8">
						<button
							v-if="!showCreateForm"
							class="px-6 py-3 rounded-lg font-semibold transition-colors font-display text-cyan-950 border border-cyan-400/50"
							style="background: linear-gradient(180deg, #22d3ee 0%, #0891b2 100%); box-shadow: 0 0 16px rgba(34, 211, 238, 0.4);"
							@click="showCreateForm = true"
						>
							Create New Game
						</button>

						<!-- Create form (inline) -->
						<div
							v-else
							class="p-5 rounded-xl max-w-md space-y-4 cyber-panel border-cyan-500/30"
							style="background: var(--cyber-panel);"
						>
							<h3 class="font-semibold font-display text-cyan-100">New Game</h3>

							<div v-if="!playerCountFixed" class="space-y-2">
								<label class="block text-sm text-slate-400">Number of Players</label>
								<div class="flex items-center gap-3">
									<button
										class="w-9 h-9 rounded-lg text-lg font-bold transition-colors disabled:opacity-40 border border-cyan-500/30 hover:border-cyan-500/50"
										style="background: var(--cyber-panel);"
										:disabled="numPlayers <= gameDef.minPlayers"
										@click="numPlayers--"
									>
										-
									</button>
									<span class="text-xl font-bold w-6 text-center text-cyan-200">{{ numPlayers }}</span>
									<button
										class="w-9 h-9 rounded-lg text-lg font-bold transition-colors disabled:opacity-40 border border-cyan-500/30 hover:border-cyan-500/50"
										style="background: var(--cyber-panel);"
										:disabled="numPlayers >= effectiveMaxPlayers"
										@click="numPlayers++"
									>
										+
									</button>
									<span class="text-sm text-slate-500 ml-1"
										>({{ gameDef.minPlayers }}&ndash;{{ effectiveMaxPlayers }})</span
									>
								</div>
							</div>
							<div v-else class="text-sm text-slate-400">{{ gameDef.maxPlayers }} players</div>

							<!-- Setup options (driven by gameDef.setupOptions) -->
							<template v-if="gameDef.setupOptions?.length">
								<div v-for="opt in gameDef.setupOptions" :key="opt.id" class="space-y-1">
									<label
										v-if="opt.type === 'boolean'"
										class="flex items-center gap-3 cursor-pointer select-none"
									>
										<button
											type="button"
											role="switch"
											:aria-checked="!!setupData[opt.id]"
											class="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800"
											:class="setupData[opt.id] ? 'bg-blue-600' : 'bg-slate-600'"
											@click="setupData[opt.id] = !setupData[opt.id]"
										>
											<span
												class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform"
												:class="setupData[opt.id] ? 'translate-x-5' : 'translate-x-0'"
											/>
										</button>
										<span class="text-sm font-medium">{{ opt.label }}</span>
									</label>
									<p v-if="opt.description" class="text-xs text-slate-500 ml-14">
										{{ opt.description }}
									</p>
								</div>
							</template>

							<!-- Your color (when creating) -->
							<div class="space-y-2">
								<label class="block text-sm text-slate-400">Your color</label>
								<div class="flex flex-wrap gap-2">
									<button
										v-for="c in PLAYER_COLORS"
										:key="c"
										type="button"
										class="w-9 h-9 rounded-full border-2 transition-all shrink-0"
										:class="[
											selectedColor === c
												? 'border-white scale-110 ring-2 ring-white/50'
												: 'border-slate-600 hover:border-slate-500',
											{
												red: 'bg-red-500',
												blue: 'bg-blue-500',
												green: 'bg-green-500',
												yellow: 'bg-yellow-400',
												black: 'bg-slate-700',
											}[c],
										]"
										:title="c"
										@click="selectedColor = c"
									/>
								</div>
							</div>

							<div class="flex gap-2">
								<button
									class="px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 font-display text-cyan-950 border border-cyan-400/50"
									style="background: linear-gradient(180deg, #22d3ee 0%, #0891b2 100%);"
									:disabled="creating"
									@click="createMatch"
								>
									{{ creating ? 'Creating...' : 'Create' }}
								</button>
								<button
									class="px-5 py-2.5 rounded-lg text-sm font-medium transition-colors border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10"
									style="background: var(--cyber-panel);"
									@click="showCreateForm = false"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>

					<!-- Two-column layout -->
					<div class="grid md:grid-cols-2 gap-8">
						<!-- Your Games -->
						<section>
							<h2 class="text-lg font-semibold mb-4 font-display text-cyan-200">Your Games</h2>

							<div
								v-if="myGames.length === 0"
								class="p-6 rounded-xl text-center cyber-panel border-cyan-500/20"
								style="background: var(--cyber-panel);"
							>
								<p class="text-slate-500 text-sm">No active games yet. Create or join one!</p>
							</div>

							<div v-else class="space-y-3">
								<div
									v-for="game in myGames"
									:key="game.matchID"
									class="rounded-xl overflow-hidden cyber-panel border-cyan-500/30 cyber-panel-hover"
									style="background: var(--cyber-panel);"
								>
									<div class="p-4">
										<div class="flex items-center gap-2 mb-1">
											<span
												class="inline-block w-2 h-2 rounded-full"
												:class="{
													'bg-yellow-400': matchStatus(game) === 'waiting',
													'bg-green-400': matchStatus(game) === 'playing',
													'bg-slate-500': matchStatus(game) === 'finished',
												}"
											/>
											<span class="text-sm font-medium capitalize">{{ matchStatus(game) }}</span>
											<span class="text-xs text-slate-500 ml-auto"
												>{{ seatSummary(game.players) }} players</span
											>
										</div>
										<p class="text-xs text-slate-500 mt-1">
											{{
												game.players
													.filter(p => p.name)
													.map(p => p.name)
													.join(', ')
											}}
										</p>
									</div>
									<div class="flex border-t border-cyan-500/20 divide-x divide-cyan-500/20">
										<button
											class="flex-1 px-3 py-2 text-xs font-medium text-cyan-400 hover:bg-cyan-500/10 transition-colors"
											@click="router.push(`/game/${game.matchID}/${game.myPlayerID}`)"
										>
											Resume
										</button>
										<button
											v-if="matchStatus(game) === 'waiting' && game.myPlayerID === '0'"
											class="flex-1 px-3 py-2 text-xs font-medium text-slate-400 hover:bg-cyan-500/10 transition-colors"
											@click="fillBotsForGame(game.matchID)"
										>
											Add Bots
										</button>
										<button
											v-if="game.myPlayerID === '0'"
											class="flex-1 px-3 py-2 text-xs font-medium text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
											@click="abandonGame(game.matchID)"
										>
											Abandon
										</button>
									</div>
								</div>
							</div>
						</section>

						<!-- Open Games -->
						<section>
							<h2 class="text-lg font-semibold mb-4 font-display text-cyan-200">Open Games</h2>

							<div
								v-if="openGames.length === 0"
								class="p-6 rounded-xl text-center cyber-panel border-cyan-500/20"
								style="background: var(--cyber-panel);"
							>
								<p class="text-slate-500 text-sm">No open games right now.</p>
							</div>

							<div v-else class="space-y-3">
								<div
									v-for="game in openGames"
									:key="game.matchID"
									class="p-4 rounded-xl cyber-panel border-cyan-500/30"
									style="background: var(--cyber-panel);"
								>
									<div class="flex items-center justify-between">
										<div>
											<p class="text-sm font-medium">{{ seatSummary(game.players) }} players</p>
											<p class="text-xs text-slate-500 mt-0.5">
												{{
													game.players
														.filter(p => p.name)
														.map(p => p.name)
														.join(', ') || 'No players yet'
												}}
											</p>
										</div>
										<button
											class="px-4 py-2 rounded-lg text-sm font-semibold transition-colors font-display text-cyan-950 border border-cyan-400/50"
											style="background: linear-gradient(180deg, #22d3ee 0%, #0891b2 100%); box-shadow: 0 0 12px rgba(34, 211, 238, 0.3);"
											@click="openJoinModal(game)"
										>
											Join
										</button>
									</div>
								</div>
							</div>
						</section>
					</div>
				</template>

				<!-- HOSTING MODE (waiting room) -->
				<template v-else-if="viewMode === 'hosting'">
					<button
						class="mb-6 text-sm text-cyan-400/80 hover:text-cyan-300 transition-colors"
						@click="backToBrowse"
					>
						&larr; Back to lobby
					</button>

					<div class="max-w-md mx-auto space-y-6">
						<div class="p-5 rounded-xl space-y-4 cyber-panel border-cyan-500/30" style="background: var(--cyber-panel);">
							<div class="flex items-center gap-2">
								<span class="inline-block w-2 h-2 rounded-full bg-green-400" style="box-shadow: 0 0 8px rgba(74, 222, 128, 0.8);" />
								<h3 class="font-semibold font-display text-cyan-100">Game Created</h3>
							</div>

							<div class="space-y-1">
								<label class="block text-xs text-slate-500">Invite Link</label>
								<div class="flex gap-2">
									<code
										class="flex-1 px-3 py-2 rounded-lg text-sm text-cyan-400 truncate border border-cyan-500/30"
										style="background: var(--cyber-bg);"
									>
										{{ inviteLink() }}
									</code>
									<button
										class="px-3 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10"
										style="background: var(--cyber-panel);"
										@click="copyLink"
									>
										{{ linkCopied ? 'Copied!' : 'Copy' }}
									</button>
								</div>
							</div>
						</div>

						<div class="p-5 rounded-xl space-y-3 cyber-panel border-cyan-500/30" style="background: var(--cyber-panel);">
							<div class="flex items-center justify-between">
								<span class="text-sm font-medium text-slate-400">Players</span>
								<span class="text-sm text-slate-500"
									>{{ filledCount }} / {{ hostedPlayers.length }}</span
								>
							</div>
							<ul class="space-y-2">
								<li
									v-for="p in hostedPlayers"
									:key="p.id"
									class="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-cyan-500/20"
									style="background: var(--cyber-bg);"
								>
									<span
										class="w-2 h-2 rounded-full shrink-0"
										:class="
											!p.name
												? 'bg-slate-600'
												: ((
														{
															red: 'bg-red-500',
															blue: 'bg-blue-500',
															green: 'bg-green-500',
															yellow: 'bg-yellow-400',
															black: 'bg-slate-700',
														} as Record<string, string>
													)[p.data?.color ?? ''] ?? 'bg-green-400')
										"
									/>
									<span class="text-sm" :class="p.name ? 'text-white' : 'text-slate-600'">
										{{ p.name || 'Waiting...' }}
									</span>
									<span v-if="p.id === 0" class="ml-auto text-xs text-slate-500">Host</span>
								</li>
							</ul>
						</div>

						<button
							class="w-full py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-display text-cyan-950 border border-cyan-400/50"
							style="background: linear-gradient(180deg, #22d3ee 0%, #0891b2 100%); box-shadow: 0 0 16px rgba(34, 211, 238, 0.4);"
							:disabled="!allSeatsFilled"
							@click="startGame"
						>
							{{
								allSeatsFilled
									? 'Start Game'
									: `Waiting for players (${filledCount}/${hostedPlayers.length})...`
							}}
						</button>

						<div v-if="!allSeatsFilled" class="flex gap-2">
							<button
								class="flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10"
								style="background: var(--cyber-panel);"
								:disabled="fillingBots"
								@click="fillWithBots"
							>
								{{ fillingBots ? 'Adding...' : 'Fill with Bots' }}
							</button>
							<button
								class="flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors border border-red-500/30 text-red-400/80 hover:bg-red-500/10 hover:text-red-400"
								style="background: var(--cyber-panel);"
								@click="abandonHostedGame"
							>
								Abandon Game
							</button>
						</div>
					</div>
				</template>
			</template>
		</main>

		<!-- Join game: pick color -->
		<Teleport to="body">
			<div
				v-if="joinModalMatchID"
				class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
				@click.self="closeJoinModal"
			>
				<div
					class="rounded-xl p-6 shadow-xl max-w-sm w-full space-y-4 cyber-panel border-cyan-500/30"
					style="background: var(--cyber-panel);"
				>
					<h3 class="text-lg font-semibold font-display text-cyan-100">Pick your color</h3>
					<div class="flex flex-wrap gap-2">
						<button
							v-for="c in joinModalAvailableColors"
							:key="c"
							type="button"
							class="w-10 h-10 rounded-full border-2 transition-all shrink-0"
							:class="[
								joinModalColor === c
									? 'border-cyan-400 scale-110 ring-2 ring-cyan-400/50'
									: 'border-cyan-500/30 hover:border-cyan-500/50',
								{
									red: 'bg-red-500',
									blue: 'bg-blue-500',
									green: 'bg-green-500',
									yellow: 'bg-yellow-400',
									black: 'bg-slate-700',
								}[c],
							]"
							:title="c"
							@click="joinModalColor = c"
						/>
					</div>
					<div class="flex gap-2 pt-2">
						<button
							class="flex-1 py-2.5 rounded-lg font-medium text-cyan-950 transition-colors font-display border border-cyan-400/50"
							style="background: linear-gradient(180deg, #22d3ee 0%, #0891b2 100%);"
							@click="joinModalMatchID && joinGame(joinModalMatchID, joinModalColor)"
						>
							Join game
						</button>
						<button
							class="px-4 py-2.5 rounded-lg font-medium text-cyan-200 transition-colors border border-cyan-500/30 hover:bg-cyan-500/10"
							style="background: var(--cyber-panel);"
							@click="closeJoinModal"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>
